<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <ProgressBar v-if="!isLiteView()" />
    <PageHeading
      v-if="hasMultipleSigners && !rc_accountStatus"
      default-heading="Your account is almost ready"
    />
    <PageHeading
      v-if="rc_accountStatus === 'SUCCESSFUL' && rc_accountNumber"
      default-heading="Congratulations, your account is open."
    />
    <PageHeading
      v-if="rc_accountStatus !== 'SUCCESSFUL' && rc_accountStatus"
      default-heading="Your application has been submitted"
    />
    <form
      ref="nurForm"
      :action="pd_envConfig.nurURL"
      method="POST"
      @submit.prevent
    >
      <div v-if="rc_accountStatus === 'SUCCESSFUL' && rc_accountNumber">
        <div class="pvd-container container-divider">
          <h2
            v-if="!!rc_accountNumber"
            class="pvd-heading-L"
          >
            Your account number is {{ rc_accountNumber }}
          </h2>
          <p v-if="!uc_isCustomer && !uc_hasCustomerCredentials && !!rc_accountNumber">
            Now, you'll set up your username and password to access your account online.
          </p>
          <p v-if="(uc_isCustomer || uc_hasCustomerCredentials) && !!rc_accountNumber">
            Now you'll be able to fund your account and review any next steps to take on the following screen.
          </p>
          <!-- NUR hidden form fields -->
          <input
            ref="ssn"
            type="hidden"
            name="ssn"
            value=""
          >
          <input
            ref="dob"
            type="hidden"
            name="dob"
            value=""
          >
          <input
            ref="zip"
            type="hidden"
            name="zip"
            value=""
          >
          <input
            ref="account"
            type="hidden"
            name="account"
            value=""
          >
          <input
            type="hidden"
            name="redirectURL"
            :value="authRedirectUrl"
          >
        </div>
      </div>
      <div
        v-if="rc_accountStatus !== 'SUCCESSFUL' && rc_accountStatus"
        id="nigoFinalPage"
        class="pvd-container"
      >
        <h2
          id="nigoHeading"
          class="pvd-heading-L"
        >
          Thank you for applying
        </h2>
        <p
          id="nigoFirstlede"
        >
          We're currently processing your application. This typically takes 1 to 2 business days,
          but may take longer. We'll contact you if additional information is required.
        </p>
        <p
          id="nigoSecondlede"
        >
          When your account is open and ready to fund, we'll reach out to you.
        </p>
        <p>
          <a
            id="fidLink"
            href="fidelity.com"
            @click="handleNIGOLinkClick"
          >
            Go to Fidelity.com
          </a>
        </p>
      </div>
      <div v-if="hasMultipleSigners && !rc_accountStatus">
        <div class="pvd-container container-divider">
          <p>
            Before we can open your account, all documents must be signed by all parties. We've
            sent emails on your behalf to all trustees asking for their signatures. Once we have
            them, we'll notify you to complete the application.
            <b>You must return and submit the application to complete it.</b>
            You can track the signatures on the next screen.
          </p>
        </div>
      </div>
      <PageNavigation
        v-if="(rc_accountNumber && rc_accountStatus === 'SUCCESSFUL') || (hasMultipleSigners && !rc_accountStatus)"
        primary-button-type="button"
        :show-primary-button-spinner="shouldShowLoadingSpinner"
        :disable-primary-button="shouldDisableNext"
        :primary-button-label="primaryButtonLabel"
        show-secondary-button="N"
        :page-exit-url="pd_content.overlay_exit_link.overview_url"
        @primary-button-clicked="nextButtonClicked"
      />
    </form>
  </div>
</template>

<script>
import dataHelper from '../mixins/dataHelper';
import PageNavigation from '../components/PageNavigation.vue';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import PageHeading from '../components/PageHeading.vue';
import ProgressBar from '../components/ProgressBar.vue';
import measurement from '../mixins/measurement';
import utility from '../mixins/utility';

export default {
  name: 'TrustFinalPage',
  components: {
    PageHeading,
    ProgressBar,
    PageNavigation,
  },
  mixins: [dataHelper, measurement, utility, pageNavigationHelper],
  data() {
    return {
      additionalUsers: [],
      hasMultipleSigners: false,
      backButtonAttempt: false,
      isLoading: false,
      recordId: '',
    };
  },
  computed: {
    shouldDisableNext() {
      return this.isLoading ? 'Y' : 'N';
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    primaryButtonLabel() {
      return this.isLoading ? this.pd_content.primary_button.wait : this.pd_content.primary_button.default;
    },
    authRedirectUrl() {
      if (!this.hasMultipleSigners) {
        return `${document.location.origin}/ftgw/digital/portfolio/summary?REBUILD_COOKIE=Y`;
      }
      return `${document.location.origin}${document.location.pathname}${document.location.search}#/retrieve`;
    },
  },
  async mounted() {
    this.track(this.cs_stateName, 'pageview');
    document.title = this.setTitle('Congratulations, your account is almost ready');
    this.recordId = this.getInstanceData()?.storageInfo?.recordId;
    this.isLoading = false;

    this.additionalUsers = this.getInstanceData().formData.additionalUsers;
    this.hasMultipleSigners = this.additionalUsers.filter((person) => person.role === 'trustee' || person.role === 'trustee/grantor').length > 0;

    this.configureBackButtonFunctionality(() => this.showBackWarning(
      this.pd_content.back_button_disabled_warning,
    ));

    try {
      if (!this.uc_isCustomer) {
        const url = this.pd_envConfig.ecaapPasswordStatusUrl;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            AppId: 'ACCOUNT-OPENING-EXP',
            AppName: 'ACCOUNT-OPENING-EXP',
            'Accept-Token-Type': 'FAC',
            'Accept-Token-Location': 'HEADER',
            'Token-Location': 'HEADER',
          },
          body: JSON.stringify({}),
        });
        const data = await response.json();
        const hasCredentials = data?.responseBaseInfo?.status?.message === 'Password Available';
        this.uc_hasCustomerCredentials = hasCredentials;
        sessionStorage.setItem('hasCustomerCredentials', JSON.stringify(hasCredentials));
      }
    } catch (error) {
      console.error('Password Status API call failed on TrustFinalPage:', error);
    }
  },
  methods: {
    nextButtonClicked() {
      this.isLoading = true;
      if (!this.uc_isCustomer && !this.uc_hasCustomerCredentials && this.rc_accountNumber && this.rc_accountStatus === 'SUCCESSFUL') {
        this.performNavigation(this.pd_content.primary_button.value, true);
        this.submitNURForm();
      } else {
        this.performNavigation(this.pd_content.primary_button.value);
      }
    },
    submitNURForm() {
      if (this.pu_ssn) {
        this.$refs.ssn.value = this.pu_ssn.replace(/-/g, '');
      }
      this.$refs.dob.value = this.pu_dob;
      this.$refs.zip.value = this.pu_leg_zip;
      this.$refs.account.value = this.rc_accountNumber;
      this.$refs.nurForm.submit();
    },
    handleNIGOLinkClick() {
      window.location.assign(this.pd_envConfig.nigoFidURL);
    },
  },
};
</script>

<style scoped>

</style>
